import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext.tsx';
import { PersonnelItem } from '../../types.ts';
import {
  Mail,
  Phone,
  Instagram,
  Linkedin,
  Users,
  Award,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';

interface PersonnelTeamSectionProps {
  badge?: string;
  title?: string;
  subtitle?: string;
}

export const DEFAULT_PERSONNEL: PersonnelItem[] = [
  {
    id: 'pers-1',
    name: 'Prof. Dr. Ir. Hendra Prasetyo, M.T.',
    role: 'Senior Advisor, Renewable Energy Systems',
    photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80',
    description: 'Senior professor and researcher at Institut Teknologi Bandung with 25+ years pioneering industrial solar PV, grid interconnection, and clean microgrid architectures across Southeast Asia.',
    whatsapp: '6281220008890',
    email: 'hendra.prasetyo@greenjobsitb.com',
    instagram: 'hendra.itb',
    linkedin: 'hendra-prasetyo-itb',
    order: 1,
  },
  {
    id: 'pers-2',
    name: 'Dr. Amanda Saraswati, S.T., M.Sc.',
    role: 'Head of ESG Advisory & Carbon Auditing',
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=600&q=80',
    description: 'Certified ISO 14064 lead auditor and GRI standards specialist. Has guided over 40 listed corporations in comprehensive Scope 1-3 greenhouse gas accounting and sustainability disclosures.',
    whatsapp: '6281220008891',
    email: 'amanda.saraswati@greenjobsitb.com',
    instagram: 'amanda.saraswati',
    linkedin: 'amanda-saraswati',
    order: 2,
  },
  {
    id: 'pers-3',
    name: 'Ir. Raditya Wardhana, M.Eng.',
    role: 'Director of Green Talent Placement',
    photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80',
    description: 'Oversees the university-industry recruitment pipeline connecting top engineering graduates and certified environmental analysts with multinational enterprises and clean tech ventures.',
    whatsapp: '6281220008892',
    email: 'raditya.wardhana@greenjobsitb.com',
    instagram: 'raditya.wardhana',
    linkedin: 'raditya-wardhana',
    order: 3,
  },
  {
    id: 'pers-4',
    name: 'Farah Nabila, S.T., M.Env.',
    role: 'Lead Circular Economy Consultant',
    photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80',
    description: 'Specializes in lifecycle assessment (LCA), industrial symbiosis, and corporate zero-waste roadmaps with extensive field deployment in eco-industrial parks and manufacturing clusters.',
    whatsapp: '6281220008893',
    email: 'farah.nabila@greenjobsitb.com',
    instagram: 'farah.nabila.env',
    linkedin: 'farah-nabila-env',
    order: 4,
  },
];

export default function PersonnelTeamSection({
  badge = 'OUR EXPERT ADVISORY TEAM',
  title = 'Meet Our Sustainability Specialists & Faculty Mentors',
  subtitle = 'Led by certified environmental engineers, researchers, and ESG consultants committed to sustainable transformation',
}: PersonnelTeamSectionProps) {
  const { settings } = useApp();
  const primaryColor = settings?.theme?.primaryColor || '#059669';
  const [selectedPerson, setSelectedPerson] = useState<PersonnelItem | null>(null);

  // Responsive items count: 1 on mobile (<640px), 2 on tablet (640-1023px), 4 on desktop (>=1024px)
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
    if (typeof window === 'undefined') return 4;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 4;
  });

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Touch swipe support
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Use configured personnel or fallback to realistic default
  const personnelList: PersonnelItem[] =
    settings?.personnel && settings.personnel.length > 0
      ? [...settings.personnel].sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_PERSONNEL;

  // Track viewport changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newCount = 4;
      if (width < 640) {
        newCount = 1;
      } else if (width < 1024) {
        newCount = 2;
      } else {
        newCount = 4;
      }
      setItemsPerPage(newCount);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Max sliding start index
  const maxIndex = Math.max(0, personnelList.length - itemsPerPage);
  const canSlide = personnelList.length > itemsPerPage;

  // Keep currentIndex in bounds when itemsPerPage changes
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [itemsPerPage, maxIndex, currentIndex]);

  const handlePrev = () => {
    if (!canSlide) return;
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    if (!canSlide) return;
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  // Touch gestures for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 45; // px

    if (diff > minSwipeDistance) {
      // Swiped Left -> Next
      handleNext();
    } else if (diff < -minSwipeDistance) {
      // Swiped Right -> Prev
      handlePrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <section id="personnel-team-section" className="py-12 md:py-16 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Navigation Controls */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 sm:mb-10 gap-4">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 mb-3 shadow-2xs">
              <Users className="w-3.5 h-3.5" style={{ color: primaryColor }} />
              <span
                className="text-xs font-extrabold uppercase tracking-wider"
                style={{ color: primaryColor }}
              >
                {badge}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight max-w-2xl leading-snug">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-xl leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          {/* Navigation Controls (If > 4 on desktop or > 1 on mobile) */}
          {canSlide && (
            <div className="flex items-center justify-center md:justify-end gap-2.5 shrink-0 pt-2 md:pt-0">
              <span className="text-xs font-bold text-slate-500 mr-1 hidden sm:inline-block">
                {currentIndex + 1} - {Math.min(currentIndex + itemsPerPage, personnelList.length)} of {personnelList.length}
              </span>

              <button
                type="button"
                onClick={handlePrev}
                className="w-10 h-10 rounded-full border border-slate-300 hover:border-emerald-500 bg-white hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 flex items-center justify-center shadow-xs hover:shadow-md transition-all active:scale-95 cursor-pointer"
                title="Previous Slide"
                aria-label="Previous Slide"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                type="button"
                onClick={handleNext}
                className="w-10 h-10 rounded-full border border-slate-300 hover:border-emerald-500 bg-white hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 flex items-center justify-center shadow-xs hover:shadow-md transition-all active:scale-95 cursor-pointer"
                title="Next Slide"
                aria-label="Next Slide"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Personnel Carousel / Slider Container */}
        <div
          className="relative overflow-hidden -mx-2.5 sm:-mx-3 py-2"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)`,
            }}
          >
            {personnelList.map((person) => (
              <div
                key={person.id}
                className="shrink-0 px-2.5 sm:px-3"
                style={{ width: `${100 / itemsPerPage}%` }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedPerson(person)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedPerson(person);
                    }
                  }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col h-full group cursor-pointer text-left focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                >
                  {/* Photo Container */}
                  <div className="relative aspect-4/5 overflow-hidden bg-slate-100">
                    <img
                      src={
                        person.photo ||
                        'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=600&q=80'
                      }
                      alt={person.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />

                    {/* Hover overlay hint */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white pointer-events-none">
                      <span className="text-[11px] font-bold text-emerald-300 mb-2 flex items-center gap-1">
                        <span>🔍 Click for full bio</span>
                      </span>

                      {/* Quick Social icons inside hover */}
                      <div
                        className="flex items-center gap-2 pointer-events-auto"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {person.email && (
                          <a
                            href={`mailto:${person.email}`}
                            className="w-8 h-8 rounded-full bg-white/90 text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-colors"
                            title="Send Email"
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                        )}
                        {person.instagram && (
                          <a
                            href={
                              person.instagram.startsWith('http')
                                ? person.instagram
                                : `https://instagram.com/${person.instagram.replace(/^@/, '')}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-pink-600 text-white flex items-center justify-center hover:bg-pink-500 shadow-md transition-colors"
                            title="Instagram"
                          >
                            <Instagram className="w-4 h-4" />
                          </a>
                        )}
                        {person.linkedin && (
                          <a
                            href={
                              person.linkedin.startsWith('http')
                                ? person.linkedin
                                : `https://linkedin.com/in/${person.linkedin}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-full bg-sky-700 text-white flex items-center justify-center hover:bg-sky-600 shadow-md transition-colors"
                            title="LinkedIn"
                          >
                            <Linkedin className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Info Body */}
                  <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-emerald-700 transition-colors line-clamp-2">
                        {person.name}
                      </h3>

                      {person.role && (
                        <p className="text-xs font-semibold text-emerald-700 mt-1 line-clamp-1">
                          {person.role}
                        </p>
                      )}

                      {/* Description: Clean line-clamp without cumbersome inline toggle */}
                      <div className="mt-2.5">
                        <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">
                          {person.description}
                        </p>
                      </div>
                    </div>

                    {/* Bottom Action bar */}
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-[11px] font-extrabold text-emerald-700 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                        <Eye className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Full bio →</span>
                      </span>

                      {/* Social indicator counts if available */}
                      <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                        {person.linkedin && <Linkedin className="w-3.5 h-3.5 hover:text-sky-600" />}
                        {person.instagram && <Instagram className="w-3.5 h-3.5 hover:text-pink-600" />}
                        {person.email && <Mail className="w-3.5 h-3.5 hover:text-slate-700" />}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carousel Pagination Dots & Mobile Indicator */}
        {canSlide && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
            <div className="flex items-center gap-1.5">
              {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    currentIndex === idx
                      ? 'w-6 h-2 bg-emerald-600'
                      : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Personnel Full Profile & Description Modal (Portaled directly to document body) */}
      {selectedPerson &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[999999] flex items-center justify-center p-3 sm:p-5 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setSelectedPerson(null)}
          >
            <div
              className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header Bar - Cleaned: Removed role and 'Personnel Profile & Credentials' */}
              <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Profil Personel
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPerson(null)}
                  className="p-1.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                  title="Close"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 sm:p-7 overflow-y-auto space-y-6 flex-1">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6">
                  {/* Photo */}
                  <div className="w-32 h-40 sm:w-40 sm:h-48 rounded-2xl overflow-hidden bg-slate-100 border-2 border-slate-200 shadow-md shrink-0">
                    <img
                      src={
                        selectedPerson.photo ||
                        'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=600&q=80'
                      }
                      alt={selectedPerson.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Key Info */}
                  <div className="flex-1 text-center sm:text-left space-y-2">
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900">
                      {selectedPerson.name}
                    </h3>

                    {selectedPerson.role && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-bold">
                        <Award className="w-3.5 h-3.5 text-emerald-600" />
                        <span>{selectedPerson.role}</span>
                      </div>
                    )}

                    {/* Social & Contact Buttons (Official Channels) */}
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-2">
                      {selectedPerson.phone && (
                        <a
                          href={`tel:${selectedPerson.phone}`}
                          className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold flex items-center gap-1.5 transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5 text-slate-600" />
                          <span>{selectedPerson.phone}</span>
                        </a>
                      )}
                      {selectedPerson.email && (
                        <a
                          href={`mailto:${selectedPerson.email}`}
                          className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold flex items-center gap-1.5 transition-colors"
                        >
                          <Mail className="w-3.5 h-3.5 text-slate-600" />
                          <span>Email</span>
                        </a>
                      )}
                      {selectedPerson.instagram && (
                        <a
                          href={
                            selectedPerson.instagram.startsWith('http')
                              ? selectedPerson.instagram
                              : `https://instagram.com/${selectedPerson.instagram.replace(/^@/, '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-xl bg-pink-50 hover:bg-pink-100 text-pink-700 border border-pink-200 text-xs font-bold flex items-center gap-1.5 transition-colors"
                        >
                          <Instagram className="w-3.5 h-3.5" />
                          <span>Instagram</span>
                        </a>
                      )}
                      {selectedPerson.linkedin && (
                        <a
                          href={
                            selectedPerson.linkedin.startsWith('http')
                              ? selectedPerson.linkedin
                              : `https://linkedin.com/in/${selectedPerson.linkedin}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 text-xs font-bold flex items-center gap-1.5 transition-colors"
                        >
                          <Linkedin className="w-3.5 h-3.5" />
                          <span>LinkedIn</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Full Description / Biography Section - Replaced header with 'Full bio' */}
                <div className="pt-4 border-t border-slate-100 space-y-2.5">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-emerald-600" />
                    <span>Full bio</span>
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200 whitespace-pre-line font-normal">
                    {selectedPerson.description ||
                      'No profile biography available for this advisory member.'}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedPerson(null)}
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                >
                  Tutup Profil
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </section>
  );
}
